<script setup>
/* eslint camelcase: off */
import debounce from 'lodash.debounce';
import {
  formatCloudCover,
  formatPercent,
  formatPrecip,
  formatRelativeHumidity,
  formatWind,
  getAlertColorForeground,
  getAlertColorPrimary,
  getElevationUnits,
  getLastItem,
  getPrecipUnits,
  getSnowUnits,
  getTemperatureUnits,
  getWindUnits,
} from '@@/utils/CommonUtils';
import { useMetaStore } from '@@/stores/Meta';
import { useUserStore } from '@@/stores/User';

const props = defineProps({
  location: {
    type: Object,
    default: null,
  },
});

const $emit = defineEmits(['fetch-history-snow-detail']);

const { $dayjs } = useNuxtApp();
const $style = useCssModule();
const metaStore = useMetaStore();
const userStore = useUserStore();

const {
  getFeelsLikeAlert,
  getSnowLevelAlert,
  getWindGustAlert,
  getWindAlert,
} = metaStore;

const units = computed(() => userStore.preferences.units);

const cloudCovers = ref([]);
const conditions = ref([]);
const days = ref([]);
const hours = ref([]);
const pops = ref([]);
const precipAccums = ref([]);
const precipSnows = ref([]);
const relativeHumidities = ref([]);
const slrLabels = ref([]);
const snowLevels = ref([]);
const tempApparents = ref([]);
const temps = ref([]);
const windGustSpeeds = ref([]);
const windSpeeds = ref([]);
const prevLinkDisabled = ref(true);
const nextLinkDisabled = ref(false);

const tableContainer = ref(null);
const table = ref(null);
const tableHeaders = ref(null);
const firstTableHeader = ref(null);

const {
  isAllAccess,
  loadingClassName,
  selectedView,
  showLoading,
  updateSelectedView,
  view,
  views,
} = useCurrentHistoryToggle(props.location);

const populateData = async () => {
  const getDayOfWeekForHour = (hour, tz) => {
    const { display_at, display_at_local_label } = hour;
    const day_of_week = $dayjs.utc(display_at).tz(tz).format('ddd');
    const dayId = `${day_of_week}-${display_at_local_label}`;
    return { day_of_week, dayId };
  };

  const field = view.value === 'Current' ? 'forecast_hourly' : 'history_hourly';
  const { timezone } = props.location;
  let previousDayOfWeek;

  props.location[field].forEach((hour, index) => {
    const {
      alerts,
      cloud_cover,
      conditions_icon_url,
      conditions_label,
      display_at_local_label,
      day_period,
      pop,
      precip_accum,
      precip_snow,
      relative_humidity,
      slr_label,
      snow_level,
      temp,
      temp_apparent,
      wind_dir_label,
      wind_gust_speed,
      wind_speed,
    } = hour;

    const { day_of_week, dayId } = getDayOfWeekForHour(hour, timezone);

    if (day_of_week !== previousDayOfWeek) {
      days.value.push({
        day: day_of_week,
        dayId,
      });
      previousDayOfWeek = day_of_week;
    }

    if (!isAllAccess && index >= 6) {
      return;
    }

    hours.value.push({
      day_of_week,
      day_period,
      display_at_local_label,
      id: dayId,
    });

    cloudCovers.value.push(formatCloudCover(cloud_cover || 0, false));

    conditions.value.push({
      conditions_icon_url,
      conditions_label,
    });

    const height = pop > 0 ? (pop * 20) : 1;
    const snowLevelAlert = getSnowLevelAlert(alerts);

    precipAccums.value.push({
      color: getAlertColorForeground(snowLevelAlert),
      precip_accum: formatPrecip(precip_accum, units.value),
    });

    precipSnows.value.push({
      color: getAlertColorForeground(snowLevelAlert),
      precip_snow: formatPrecip(precip_snow, units.value),
    });

    pops.value.push({
      backgroundColor: getAlertColorPrimary(snowLevelAlert),
      color: getAlertColorForeground(snowLevelAlert),
      height: `${height}px`,
      pop: formatPercent(pop || 0),
    });

    const feelsLikeAlert = getFeelsLikeAlert(alerts);
    const color = getAlertColorPrimary(feelsLikeAlert);
    tempApparents.value.push({ color, tempApparent: Math.round(temp_apparent) });

    temps.value.push(Math.round(temp));

    relativeHumidities.value.push(formatRelativeHumidity(relative_humidity, false));

    slrLabels.value.push({
      color: getAlertColorForeground(snowLevelAlert),
      slr_label,
    });

    snowLevels.value.push({ alerts, snow_level });

    const windGustAlert = getWindGustAlert(alerts);

    windGustSpeeds.value.push({
      color: getAlertColorForeground(windGustAlert),
      wind_gust_speed: formatWind(wind_gust_speed, units.value, false),
    });

    const windAlert = getWindAlert(alerts);

    windSpeeds.value.push({
      color: getAlertColorForeground(windAlert),
      wind_speed: formatWind(wind_speed, units.value, false, wind_dir_label),
    });
  });

  if (view.value === 'Current') {
    days.value[0].day = 'Now';
  }
  else {
    const lastHour = getLastItem(props.location[field]);
    const { dayId } = getDayOfWeekForHour(lastHour, timezone);

    const lastDay = getLastItem(days.value);
    lastDay.day = 'Now';
    lastDay.dayId = dayId;
  }
};

const elevationUnits = computed(() => getElevationUnits(units.value));
const linkDisabledClass = 'text-light-color tw-pointer-events-none';
const precipUnits = computed(() => getPrecipUnits(units.value));
const sectionBorderClass = 'tw-border-b graph-divider-border-color';

const slrAttributionDetailUrl = computed(() => metaStore.getAttributionDetailUrl('cloudnine', 'slr')
  || '#');

const snowUnits = computed(() => getSnowUnits(units.value));
const tableCellStyle = 'tw-w-12 tw-px-px';

/**
 * If the user is All-Access set the table width to accommodate approximately 120 hours of
 * data. If the user is not All-Access let the width be auto since only 6 hours of data will
 * be shown. Setting the table width is necessary in order for the browser to calculate the
 * table cell size properly when using a fixed table layout.
 * @see https://css-tricks.com/almanac/properties/t/table-layout/
 */
const tableStyle = computed(() => {
  if (isAllAccess) {
    // 115 is width of sticky column + 120 hours of data * each column which is is 48px wide
    // (tw-w-12) with 2px of horizontal padding (tw-px-px).
    const minWidth = `${115 + (120 * (48 + 2))}px`;
    return { minWidth };
  }

  return null;
});

const tableHeaderClass = [
  $style.tableHeader,
  'tw-w-24 tw-pr-4 tw-py-0.5 tw-text-left tw-whitespace-nowrap tw-sticky tw-left-0 tw-z-10 card-background-color',
];

const temperatureUnits = computed(() => getTemperatureUnits(units.value));
const windUnits = computed(() => getWindUnits(units.value));

const getNightClass = (index) => hours.value?.[index]?.day_period === 'night' ? 'card-alternate-row-background-color' : '';

const getTableDataClass = (index, height = 'tw-h-8') => `${tableCellStyle} ${height} tw-py-0.5 tw-text-center ${getNightClass(index)}`;

const getVisibleTableHeaderIndex = () => {
  const { scrollLeft } = tableContainer.value;
  const firstTableHeaderWidth = firstTableHeader.value.offsetWidth;
  const scrollLeftAdjusted = scrollLeft + firstTableHeaderWidth;

  return tableHeaders.value.findIndex((tableHeader) => tableHeader.offsetLeft >= scrollLeftAdjusted);
};

const handleContainerScroll = debounce(() => {
  const { scrollLeft } = tableContainer.value || {};

  if (typeof scrollLeft !== 'number') {
    return;
  }

  const tableContainerWidth = Math.round(tableContainer.value.offsetWidth);
  const tableWidth = Math.round(table.value.offsetWidth);

  // Disable the previous link if the table cannot be scrolled any further to the left
  prevLinkDisabled.value = scrollLeft === 0;

  // Disable the next link if the table cannot be scrolled any further to the right
  nextLinkDisabled.value = (Math.ceil(scrollLeft) + tableContainerWidth) - tableWidth === 0;
}, 250);

const scrollToDay = (dayId, behavior = 'smooth') => {
  const isSticky = getComputedStyle(firstTableHeader.value).position === 'sticky';
  const th = document.querySelector(`#${dayId}`);

  if (!th) {
    return;
  }

  let left = th.offsetLeft;

  if (isSticky) {
    left -= Math.round(firstTableHeader.value.offsetWidth);
  }

  tableContainer.value.scrollTo({ behavior, left, top: 0 });
};

const handleNextLinkClick = () => {
  const index = getVisibleTableHeaderIndex();
  let nextIndex = index + 12;

  if (nextIndex >= tableHeaders.value.length) {
    nextIndex = tableHeaders.value.length - 1;
  }

  const dayId = tableHeaders.value[nextIndex].id;
  scrollToDay(dayId);
};

const handlePrevLinkClick = () => {
  const index = getVisibleTableHeaderIndex();
  let prevIndex = index - 12;

  if (prevIndex < 0) {
    prevIndex = 0;
  }

  const dayId = tableHeaders.value[prevIndex].id;
  scrollToDay(dayId);
};

const resetData = () => {
  cloudCovers.value = [];
  conditions.value = [];
  days.value = [];
  hours.value = [];
  pops.value = [];
  precipAccums.value = [];
  precipSnows.value = [];
  relativeHumidities.value = [];
  slrLabels.value = [];
  snowLevels.value = [];
  tempApparents.value = [];
  temps.value = [];
  windGustSpeeds.value = [];
  windSpeeds.value = [];
};

const handleSelectView = async ($event, newView) => {
  const canUpdateView = updateSelectedView(newView);

  if (canUpdateView) {
    const dayId = tableHeaders.value[0].id;
    scrollToDay(dayId, 'instant');

    if (view.value === 'History'
      && (!props.location.history_hourly || !props.location.history_semi_daily)) {
      await nextTick();
      $emit('fetch-history-snow-detail');
    }
    else {
      resetData();
      populateData();
    }
  }
};

watch(() => props.location, () => {
  resetData();
  populateData();
  showLoading.value = false;
}, { deep: true });

populateData();
</script>

<template>
  <Card
    :has-body-padding-small="true"
  >
    <template #header>
      <div class="tw-flex tw-flex-col">
        <h2 class="section-title heading-color tw-mb-4">
          5-Day Hourly Forecast
        </h2>
        <Pills
          :pills="views"
          :selected-pill="selectedView"
          @click="handleSelectView"
        />
      </div>
    </template>
    <template #body>
      <transition
        enter-active-class="fade-enter-transition"
        enter-from-class="fade-out"
        leave-active-class="fade-leave-transition"
        leave-to-class="fade-out"
      >
        <div
          v-if="showLoading"
          :class="loadingClassName"
        >
          <LoadingIndicator />
        </div>
      </transition>
      <div class="tw-font-normal">
        <div class="tw-flex tw-justify-center lg:tw-justify-between tw-text-sm tw-py-2 lg:tw-pl-28">
          <a
            :class="[
              prevLinkDisabled ? linkDisabledClass : 'link-color-brand',
              'tw-hidden lg:tw-inline-block',
            ]"
            href="javascript:void(0)"
            @click="handlePrevLinkClick"
          >
            <font-awesome-icon :icon="['fas', 'arrow-alt-circle-left']" />
            Back 12 Hours
          </a>
          <div>
            <span>Jump To:</span>
            <ul class="tw-inline-block">
              <li
                v-for="{ day, dayId } in days"
                :key="day"
                class="tw-inline-block tw-mx-1"
              >
                <a
                  class="link-color-brand"
                  href="javascript:void(0);"
                  @click="scrollToDay(dayId)"
                >
                  {{ day }}
                </a>
              </li>
            </ul>
          </div>
          <a
            :class="[
              nextLinkDisabled ? linkDisabledClass : 'link-color-brand',
              'tw-hidden lg:tw-inline-block',
            ]"
            href="javascript:void(0)"
            @click="handleNextLinkClick"
          >
            Forward 12 Hours
            <font-awesome-icon :icon="['fas', 'arrow-alt-circle-right']" />
          </a>
        </div>
        <div
          ref="tableContainer"
          :class="[
            'tw-w-full tw-overflow-x-auto',
            !isAllAccess ? 'tw-flex tw-pb-4' : null,
            'hide-scrollbar-on-mobile',
          ]"
          @scroll="handleContainerScroll"
        >
          <table
            ref="table"
            class="tw-text-xs tw-my-1.5 tw-table-fixed tw-font-normal"
            :style="tableStyle"
          >
            <tbody>
              <tr>
                <th
                  ref="firstTableHeader"
                  :class="tableHeaderClass"
                >
                  &nbsp;
                </th>
                <th
                  v-for="(hour, index) in hours"
                  :id="hour.id"
                  :key="index"
                  ref="tableHeaders"
                  :class="[
                    'tw-pt-1.5',
                    tableCellStyle,
                    getNightClass(index),
                  ]"
                >
                  <strong class="tw-block">
                    {{ hour.display_at_local_label }}
                  </strong>
                  <span class="tw-font-normal text-light-color">
                    {{ hour.day_of_week }}
                  </span>
                </th>
              </tr>
              <tr :class="sectionBorderClass">
                <th :class="tableHeaderClass">
                  <strong>Precip Chance</strong>
                </th>
                <td
                  v-for="({ backgroundColor, height, color, pop }, index) in pops"
                  :key="index"
                  :class="[getTableDataClass(index, 'tw-h-11'), 'tw-align-bottom']"
                >
                  <span
                    class="tw-block tw-text-xs"
                    :style="{ color }"
                  >
                    {{ pop }}
                  </span>
                  <div
                    class="tw-rounded-t tw-w-11/12 tw-mb-0.5 tw-mx-auto"
                    :style="{ backgroundColor, height }"
                  />
                </td>
              </tr>
              <tr>
                <th :class="tableHeaderClass">
                  <strong>Precipitation</strong>
                  <span class="tw-font-normal text-light-color">
                    {{ precipUnits }}
                  </span>
                </th>
                <td
                  v-for="({ color, precip_accum }, index) in precipAccums"
                  :key="index"
                  :class="getTableDataClass(index)"
                  :style="{ color }"
                >
                  <span v-if="isAllAccess">
                    {{ precip_accum }}
                  </span>
                  <font-awesome-icon
                    v-else
                    class="all-access-color"
                    :icon="['fas', 'lock']"
                  />
                </td>
              </tr>
              <tr>
                <th :class="tableHeaderClass">
                  <strong>Snowfall</strong>
                  <span class="tw-font-normal text-light-color">
                    {{ snowUnits }}
                  </span>
                </th>
                <td
                  v-for="({ color, precip_snow }, index) in precipSnows"
                  :key="index"
                  :class="getTableDataClass(index)"
                  :style="{ color }"
                >
                  <span v-if="isAllAccess">
                    {{ precip_snow }}
                  </span>
                  <font-awesome-icon
                    v-else
                    class="all-access-color"
                    icon="lock"
                  />
                </td>
              </tr>
              <tr>
                <th :class="tableHeaderClass">
                  <strong>Snow Ratio</strong>
                  <a
                    class="tw-font-normal hover:tw-underline link-color-brand"
                    :href="slrAttributionDetailUrl"
                    rel="noopener noreferrer"
                    target="_blank"
                  >SLR</a>
                </th>
                <td
                  v-for="({ color, slr_label }, index) in slrLabels"
                  :key="index"
                  :class="getTableDataClass(index)"
                  :style="{ color }"
                >
                  <span v-if="isAllAccess">
                    {{ slr_label }}
                  </span>
                  <font-awesome-icon
                    v-else
                    class="all-access-color"
                    :icon="['fas', 'lock']"
                  />
                </td>
              </tr>
              <tr :class="sectionBorderClass">
                <th :class="tableHeaderClass">
                  <strong>Snow Level</strong>
                  <span class="tw-font-normal text-light-color">
                    {{ elevationUnits }}
                  </span>
                </th>
                <td
                  v-for="(forecast, index) in snowLevels"
                  :key="index"
                  :class="getTableDataClass(index)"
                >
                  <SnowLevel
                    :forecast="forecast"
                    :location="location"
                    :show-units="false"
                  />
                </td>
              </tr>
              <tr>
                <th :class="tableHeaderClass">
                  <strong>Weather</strong>
                </th>
                <td
                  v-for="(condition, index) in conditions"
                  :key="index"
                  :class="getTableDataClass(index)"
                >
                  <img
                    :alt="condition.conditions_label"
                    class="tw-inline-block tw-w-7 tw-h-7 dark-invert-100%"
                    :src="condition.conditions_icon_url"
                  >
                </td>
              </tr>
              <tr>
                <th :class="tableHeaderClass">
                  <strong>Temperature</strong>
                  <span class="tw-font-normal text-light-color">
                    {{ temperatureUnits }}
                  </span>
                </th>
                <td
                  v-for="(temp, index) in temps"
                  :key="index"
                  :class="getTableDataClass(index)"
                >
                  {{ temp }}
                </td>
              </tr>
              <tr>
                <th :class="tableHeaderClass">
                  <strong>Feels Like</strong>
                  <span class="tw-font-normal text-light-color">
                    {{ temperatureUnits }}
                  </span>
                </th>
                <td
                  v-for="({ color, tempApparent }, index) in tempApparents"
                  :key="index"
                  :class="getTableDataClass(index)"
                  :style="{ color }"
                >
                  {{ tempApparent }}
                </td>
              </tr>
              <tr :class="sectionBorderClass">
                <th :class="tableHeaderClass">
                  <strong>Rel. Humidity</strong>
                  <span class="tw-font-normal text-light-color">
                    %
                  </span>
                </th>
                <td
                  v-for="(relativeHumidity, index) in relativeHumidities"
                  :key="index"
                  :class="getTableDataClass(index)"
                >
                  {{ relativeHumidity }}
                </td>
              </tr>
              <tr>
                <th :class="tableHeaderClass">
                  <strong>Wind Speed</strong>
                  <span class="tw-font-normal text-light-color">
                    {{ windUnits }}
                  </span>
                </th>
                <td
                  v-for="({ color, wind_speed }, index) in windSpeeds"
                  :key="index"
                  :class="getTableDataClass(index)"
                  :style="{ color }"
                >
                  {{ wind_speed }}
                </td>
              </tr>
              <tr :class="sectionBorderClass">
                <th :class="tableHeaderClass">
                  <strong>Wind Gust</strong>
                  <span class="tw-font-normal text-light-color">
                    {{ windUnits }}
                  </span>
                </th>
                <td
                  v-for="({ color, wind_gust_speed }, index) in windGustSpeeds"
                  :key="index"
                  :class="getTableDataClass(index)"
                  :style="{ color }"
                >
                  {{ wind_gust_speed }}
                </td>
              </tr>
              <tr>
                <th :class="tableHeaderClass">
                  <strong>Cloud Cover</strong>
                  <span class="tw-font-normal text-light-color">
                    %
                  </span>
                </th>
                <td
                  v-for="(cloudCover, index) in cloudCovers"
                  :key="index"
                  :class="getTableDataClass(index)"
                >
                  {{ cloudCover }}
                </td>
              </tr>
            </tbody>
          </table>
          <UpgradeToAllAccess
            v-if="!isAllAccess"
            :class="[$style.allAccessBlock, 'tw-flex-grow']"
          />
        </div>
        <SnowForecastTableFooter
          class="tw-pt-1.5 lg:tw-ml-28"
          field="forecast_hourly"
          :source-id="location.forecast_source_id"
        />
      </div>
    </template>
  </Card>
</template>

<style module>
/* SEE:  https://stackoverflow.com/questions/50361698/border-style-do-not-work-with-sticky-position-element#answer-52256954 */
.tableHeader {
  box-shadow: inset -1px 0 var(--border-color);
}

.allAccessBlock {
  min-width: 300px;
}
</style>
